import {
  FIELD_CONFIG,
  validateRubricField,
  simplifyContent,
} from "@/components/screener-builder/review/competency-modal/assessment-question-field";
import { closingPhrases } from "@/hooks/validation";
import {
  AssessmentQuestionFields,
  AssessmentQuestionResponseFormat,
  type BuilderInfoFirstDraft,
  type GeneratableFields,
  type GenerateResponse,
  type QuestionWarning,
  type ReviewPageCompetency,
  type ScreenerBuilderContext,
  type ScreenerBuilderResponse,
  ScreenerBuilderStep,
  SCORING_RUBRIC,
  type WarningBypassLog,
} from "@/types/screener-builder";
import { ScreenerStatus, ScreenerType } from "@/types/screeners";
import { AlertSeverity, type Maybe, type Nullable } from "@/types/utils";
import { isEmptyAfterTrim } from "@/utils/string";

/**
 * Statuses that indicate a screener has unpublished changes.
 * Used to determine when to show "unpublished changes" indicators.
 */
export const UNPUBLISHED_STATUSES = [
  ScreenerStatus.Draft,
  ScreenerStatus.Preview,
] as const;

/**
 * Check if a screener status indicates unpublished changes.
 * Centralizes the logic for checking draft/preview status.
 */
export const isUnpublishedStatus = (status: ScreenerStatus): boolean =>
  (UNPUBLISHED_STATUSES as readonly ScreenerStatus[]).includes(status);

// Mostly exists to prevent magic strings in case we ever change the param
export const screenerIdIsNew = (screenerId?: unknown): screenerId is "new" =>
  screenerId === "new";

// Any non "ai" screener type is considered legacy
export const isLegacyScreenerType = (screenerType: ScreenerType) =>
  screenerType !== ScreenerType.v2ai &&
  screenerType !== ScreenerType.v2aim &&
  screenerType !== ScreenerType.v30 &&
  screenerType !== ScreenerType.v30m;

// Legacy/CSV screeners only have access to the Locations and Review steps due
// to data constraints
export const onLegacyAllowedStep = (step: ScreenerBuilderStep) =>
  [ScreenerBuilderStep.Locations, ScreenerBuilderStep.Review].includes(step);

export const screenerHasReachedGenerationStep = ({
  published,
  step,
}: ScreenerBuilderResponse | BuilderInfoFirstDraft) =>
  published ||
  // Once a user has progressed past context we start generation, no updates
  // may be made after that point.
  ![ScreenerBuilderStep.Position, ScreenerBuilderStep.Context].includes(step);

export const MIN_COMPETENCIES_AND_QUESTIONS = 1;
export const MAX_INCLUDED_COMPETENCIES = 6;
export const MAX_COMPETENCIES_TOOLTIP_MESSAGE = `Interviews can only include up to ${MAX_INCLUDED_COMPETENCIES} written assessment questions`;
export const getMinimumRequiredDeleteTooltip = (name: string) =>
  `At least ${MIN_COMPETENCIES_AND_QUESTIONS} ${name} must be included`;

export const EMPTY_BUILDER_CONTEXT: ScreenerBuilderContext = {
  companyMission: null,
  companyValues: null,
  interviewGuide: null,
  otherNotes: null,
  relatedRoles: null,
  toneOfVoiceExample: null,
};

/**
 * Check if a competency has at least one selected written (non-voice) question.
 */
export const competencyHasWrittenQuestion = (
  competency: ReviewPageCompetency,
) =>
  competency.questions.some(
    (q) =>
      q.isSelected &&
      (!q.responseFormat ||
        q.responseFormat === AssessmentQuestionResponseFormat.Written),
  );

/**
 * Count included competencies that have at least one written (non-voice) question.
 * Voice-only competencies don't count toward the assessment question limit
 * because the assessment prompt only ever sees written questions — voice
 * questions are asked outside it (3.0: by the interview orchestrator's voice
 * flow; 2.0: by the screener_basic worker).
 */
export const getIncludedCompetencyCount = (
  competencies: ReviewPageCompetency[],
) =>
  competencies.filter(
    (competency) =>
      competency.isIncluded && competencyHasWrittenQuestion(competency),
  ).length;

export const screenerHasMaxIncludedCompetencies = (
  competencies: ReviewPageCompetency[],
) => getIncludedCompetencyCount(competencies) >= MAX_INCLUDED_COMPETENCIES;

/**
 * Check if a competency has at least one selected assessment question of any
 * type (voice or written).
 */
export const competencyHasAssessmentQuestion = (
  competency: ReviewPageCompetency,
) => competency.questions.some((q) => q.isSelected);

/**
 * Count included competencies that have at least one selected assessment
 * question of any type. Backs the "at least one assessment question" guardrail
 * (AS-1958) — unlike getIncludedCompetencyCount, which counts only written
 * questions for the assessment-AI max limit.
 */
export const getIncludedAssessmentCompetencyCount = (
  competencies: ReviewPageCompetency[],
) =>
  competencies.filter(
    (competency) =>
      competency.isIncluded && competencyHasAssessmentQuestion(competency),
  ).length;

export const extractClosingPhrase = (text?: string): Maybe<string> => {
  if (isEmptyAfterTrim(text)) {
    return;
  }

  // text is guaranteed to be defined and non-empty after isEmptyAfterTrim check
  const lowerText = text!.toLowerCase();
  return closingPhrases.find((phrase) => {
    const regex = new RegExp(
      `\\b${phrase.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`,
      "i",
    );
    return regex.test(lowerText);
  });
};

export const validateGeneratedContent = <T extends GeneratableFields>(
  field: T,
  content: GenerateResponse<T>["content"],
): { isValid: boolean; errors: string[] } => {
  switch (field) {
    case AssessmentQuestionFields.Competency:
    case AssessmentQuestionFields.Definition: {
      const validation = FIELD_CONFIG[
        field as
          | AssessmentQuestionFields.Competency
          | AssessmentQuestionFields.Definition
      ].validate(content as string);
      return {
        isValid: validation.valid,
        errors: validation.validationErrors,
      };
    }

    case AssessmentQuestionFields.Question: {
      const questions =
        content as GenerateResponse<AssessmentQuestionFields.Question>["content"];
      const questionErrors = questions
        .map((q, index) => {
          const validation = FIELD_CONFIG[
            AssessmentQuestionFields.Question
          ].validate(q.question);
          return validation.valid
            ? null
            : `Question ${index + 1}: ${validation.validationErrors.join(", ")}`;
        })
        .filter(Boolean) as string[];

      return {
        isValid: questionErrors.length === 0,
        errors: questionErrors,
      };
    }

    case SCORING_RUBRIC: {
      const rubric = content as { high: string; medium: string; low: string };
      const rubricErrors = [
        {
          key: "High scoring answer",
          validation: validateRubricField(rubric.high),
        },
        {
          key: "Medium scoring answer",
          validation: validateRubricField(rubric.medium),
        },
        {
          key: "Low scoring answer",
          validation: validateRubricField(rubric.low),
        },
      ]
        .map(({ key, validation }) =>
          validation.valid
            ? null
            : `${key}: ${validation.validationErrors.join(", ")}`,
        )
        .filter(Boolean) as string[];

      return {
        isValid: rubricErrors.length === 0,
        errors: rubricErrors,
      };
    }

    default:
      // For unknown fields, assume valid
      return {
        isValid: true,
        errors: [],
      };
  }
};

export const prepareGenerateRetryContext = <T extends GeneratableFields>(
  field: T,
  response: GenerateResponse<T>,
): string => {
  switch (field) {
    case AssessmentQuestionFields.Competency:
    case AssessmentQuestionFields.Definition:
      return response.content as string;
    case AssessmentQuestionFields.Question:
      return JSON.stringify(
        simplifyContent.question(
          response.content as GenerateResponse<AssessmentQuestionFields.Question>["content"],
        ),
      );
    case SCORING_RUBRIC:
      return JSON.stringify(
        response.content as {
          high: string;
          medium: string;
          low: string;
        },
      );
    default:
      return JSON.stringify(response.content);
  }
};

export const getPersistedWarning = (
  warningBypassLog: Maybe<Nullable<WarningBypassLog[]>>,
): Maybe<QuestionWarning> => {
  const warningMessage = warningBypassLog
    ?.filter((log) => !log.resolvedAt)
    ?.map((log) => log.warningMessage)
    .join("\n");

  if (!warningMessage) {
    return undefined;
  }

  return {
    message: warningMessage,
    severity: AlertSeverity.Warning,
  };
};
